import React, { useState } from 'react';
import { X, Heart } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../utils/api';

/**
 * Standalone sponsor modal — opens from the public Show page. Anyone can
 * sponsor (logged-in users OR guests). Submits to the backend which mints
 * a Stripe checkout session for the custom amount. The sponsor list itself
 * is private — only the producer sees who sponsored.
 */
export default function SponsorShowModal({ show, onClose }) {
  const minimum = Number(show?.sponsorship_minimum ?? 1) || 1;
  const [amount, setAmount] = useState(String(minimum));
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt < minimum) {
      toast.error(`Minimum sponsorship is $${minimum.toFixed(2)}`);
      return;
    }
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/shows/${show.id}/sponsorships/checkout`, {
        amount: amt,
        sponsor_name: name.trim(),
        sponsor_email: email.trim(),
        message: message.trim(),
        origin_url: window.location.origin,
      });
      // Redirect to Stripe Checkout.
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout');
        setSubmitting(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sponsorship failed');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[80] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="sponsor-modal-backdrop"
    >
      <div
        className="bg-[#161616] border border-[#262626] rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="sponsor-modal"
      >
        <div className="px-6 py-4 border-b border-[#262626] flex items-center justify-between sticky top-0 bg-[#161616]">
          <div className="flex items-center gap-2">
            <Heart size={20} weight="fill" className="text-[#FF3B30]" />
            <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
              Sponsor This Show
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-[#9E9E9E] hover:text-white"
            data-testid="sponsor-modal-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-[#9E9E9E]">
            Help out <span className="text-white font-semibold">{show?.name}</span> with any amount you choose. The producer will be notified directly.
          </p>

          <div>
            <label className="block text-xs font-semibold text-[#9E9E9E] uppercase tracking-wider mb-2">
              Amount ($) <span className="text-[#555] normal-case font-normal">(min ${minimum.toFixed(2)})</span>
            </label>
            <div className="grid grid-cols-4 gap-2 mb-2">
              {[25, 50, 100, 250].map(preset => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setAmount(String(preset))}
                  className={`px-2 py-2 rounded-md text-sm font-semibold transition-colors ${
                    Number(amount) === preset
                      ? 'bg-[#FF3B30] text-white'
                      : 'bg-[#0A0A0A] border border-[#262626] text-white hover:border-[#FF3B30]/50'
                  }`}
                  data-testid={`sponsor-preset-${preset}`}
                >
                  ${preset}
                </button>
              ))}
            </div>
            <input
              type="number"
              step="0.01"
              min={minimum}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`Custom amount`}
              className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm focus:border-[#FF3B30] focus:outline-none transition-colors"
              data-testid="sponsor-amount-input"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#9E9E9E] uppercase tracking-wider mb-2">
              Your Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm focus:border-[#FF3B30] focus:outline-none"
              data-testid="sponsor-name-input"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#9E9E9E] uppercase tracking-wider mb-2">
              Email <span className="text-[#555] normal-case font-normal">(optional)</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm focus:border-[#FF3B30] focus:outline-none"
              data-testid="sponsor-email-input"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#9E9E9E] uppercase tracking-wider mb-2">
              Message <span className="text-[#555] normal-case font-normal">(optional)</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Best of luck with the show!"
              rows={3}
              className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm focus:border-[#FF3B30] focus:outline-none resize-none"
              data-testid="sponsor-message-input"
              maxLength={500}
            />
          </div>

          <p className="text-[11px] text-[#555] leading-relaxed">
            Your contribution is processed securely via Stripe and is only visible to the show producer.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-[#262626] flex gap-3 justify-end sticky bottom-0 bg-[#161616]">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333] transition-colors"
            data-testid="sponsor-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !name.trim() || !amount || Number(amount) < minimum}
            className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-[#FF3B30] hover:bg-[#FF3B30]/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="sponsor-submit"
          >
            {submitting ? (
              'Loading…'
            ) : (
              <>
                <Heart size={16} weight="fill" />
                Sponsor ${Number(amount || 0).toFixed(2)}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
